import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { AuthService } from "app/auth/auth.service";
import {
  CompanyWorkgroup,
  UserWithWorkgroups,
} from "models/users/user-workgroups";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { UsersService } from "providers/users-service/users.service";
import { take } from "rxjs/operators";
import { commonVar } from "../../../../providers/common-var/common";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { ContactDetails } from "../contact-details";

@Component({
  selector: "account-contact-details",
  templateUrl: "account-contact-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccountContactDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AccountContactDetails),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountContactDetails extends ContactDetails implements OnInit {
  @Input() isEditMode: boolean = false;
  @Input() userId: string = "";
  @Input() isNew: boolean = false;

  _workgroupOptions = [];
  workgroupData = [];
  /** True when the work-group lookup failed. Drives the message under the Workgroup
   *  field — without it the picker is simply empty and Save is blocked with no cause
   *  shown anywhere. */
  workGroupsUnavailable: boolean = false;

  private _showChangePassword: boolean = false;
  private _saveEnabled: boolean = false;
  private _canChangePassword: boolean = false;
  private _showChangePasswordInput: boolean = false;

  emailAddress: string = "";
  randomize = require("randomatic");
  password_type: string = "password";

  constructor(
    protected formBuilder: FormBuilder,
    protected resourceManagementService: ResourceManagementService,
    protected companyManagementService: CompanyManagementService,
    protected usersService: UsersService,
    private authSvc: AuthService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      accountName: [
        "",
        [
          Validators.required,
          Validators.pattern(commonVar.EMAIL_REGEX),
          Validators.maxLength(50),
        ],
      ],
      workgroups: [null, Validators.required],
      // removed below two fields from users branch
      password: [null],
      passwordVerify: [null],
    });

    //Patch Select Value from RM Service
    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
        // removed from users branch
        this.toggleSave(value.password, value.passwordVerify);
      })
    );

    // These two loads are INDEPENDENT and must stay that way. The existing user's
    // details used to be fetched inside the getWorkGroups success callback, so when
    // that call failed the Login ID rendered blank and the change-password affordance
    // never appeared — for a reason nobody could see, because the error callback was
    // an empty function. The work-group endpoint answers HTTP 500 today (its backend,
    // users-service, is not deployed — see CO-3512), so that was the normal path.
    this.loadWorkGroups();
    this.loadExistingUser();
  }

  private loadWorkGroups(): void {
    this.companyManagementService
      .getWorkGroups(this.authSvc.getUser().tenantId)
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          this.workGroupOptions = (response || []).map((x) => {
            return {
              display: x.name,
              value: x.id,
            };
          });
          this.workGroupsUnavailable = false;
        },
        (err) => {
          // Never silent: the Workgroup field is `required`, so an empty list blocks
          // Save outright. Say so rather than leaving the user with a dropdown that
          // will not open and a form that will not submit.
          this.workGroupOptions = [];
          this.workGroupsUnavailable = true;
          console.error("Failed to load work groups", err);
        }
      );
  }

  private loadExistingUser(): void {
    if (!this.userId || this.userId === "") {
      return;
    }

    this.canChangePassword = true;

    this.usersService
      .getUser(this.userId.toString())
      .pipe()
      .take(1)
      .subscribe(
        (x: UserWithWorkgroups) => {
          const groups = ((x && x.workgroups) || []).map((z) => {
            return z.id;
          });

          this.innerFormGroup.controls["workgroups"].patchValue(groups);
          this.innerFormGroup.controls["accountName"].patchValue(
            x ? x.userName : ""
          );
        },
        (err) => {
          console.error("Failed to load the user account", err);
        }
      );
  }

  ngOnInit() {
    this.resourceManagementService.systemUserEmailAddress.subscribe((email) => {
      this.accountName.setValue(email);
      this.emailAddress = email;
    });
  }

  ngAfterViewInit() {
    this.accountName.setValue(this.emailAddress);
    // removed below IF code in users branch
    if (this.isNew) {
      this.generatePassword();
    }
  }

  cancel(): void {
    this.password.setValue("");
    this.passwordVerify.setValue("");
    this.showChangePasswordInput = false;
  }

  // users branch
  // save(): void {}
  // master branch (replace)
  save(): void {
    const password = this.password.value;
    const passwordVerify = this.passwordVerify.value;

    if (password && passwordVerify) {
      this.usersService
        .changePassword(this.userId, {
          password: password,
          passwordVerify: passwordVerify,
        })
        .pipe(take(1))
        .subscribe(
          () => {},
          (err) => {
            console.error(err);
          }
        );
    }
    this.saveEnabled = false;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid ? null : { account: { valid: false } };
  }
  get accountName() {
    return this.innerFormGroup.get("accountName") as FormControl;
  }

  get password() {
    return this.innerFormGroup.get("password") as FormControl;
  }

  get passwordVerify() {
    return this.innerFormGroup.get("passwordVerify") as FormControl;
  }

  set workGroupOptions(options: any[]) {
    this._workgroupOptions = options;
  }

  get workGroupOptions(): any[] {
    return this._workgroupOptions;
  }

  get showChangePassword(): boolean {
    return this._showChangePassword;
  }

  set showChangePassword(value: boolean) {
    this._showChangePassword = value;
  }

  get canChangePassword(): boolean {
    return this._canChangePassword;
  }

  set canChangePassword(value: boolean) {
    this._canChangePassword = value;
  }

  get saveEnabled(): boolean {
    return this._saveEnabled;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get showChangePasswordInput(): boolean {
    return this._showChangePasswordInput;
  }

  set showChangePasswordInput(value: boolean) {
    this._showChangePasswordInput = value;
  }

  // removed from users branch
  toggleSave(password: string, passwordVerify: string): void {
    if (
      password === "" ||
      passwordVerify === "" ||
      !password ||
      !passwordVerify
    ) {
      this.saveEnabled = false;
      return;
    }

    if (password === passwordVerify) {
      this.saveEnabled = true;
      return;
    }

    this.saveEnabled = false;
  }
  // removed from users branch
  togglePasswordMode() {
    this.password_type = this.password_type === "text" ? "password" : "text";
  }
  // removed from users branch
  generatePassword() {
    const pass = this.randomize("*", 8, {
      exclude: [
        "%",
        "~",
        "@",
        "^",
        "(",
        ")",
        "_",
        "+",
        "-",
        "=",
        "{",
        "}",
        "[",
        "]",
        ";",
        "\\",
        "'",
        ",",
        ".",
      ],
    });
    this.password.setValue(pass);
    this.passwordVerify.setValue(pass);
    this.password.markAsTouched();
    this.passwordVerify.markAsTouched();
    this.showChangePasswordInput = true;
  }

  // users branch
  findWorkGroupOption(e) {
    let element = undefined;
    this.workGroupOptions.forEach((elem, i) => {
      if (elem.value === e) {
        element = elem;
      }
    });

    return element;
  }

  // users branch
  get selectedWorkgroups(): FormArray {
    return this.innerFormGroup.get("workgroups") as FormArray;
  }

  // users branch
  get assignedWorkGroups(): CompanyWorkgroup[] {
    let workgroups: CompanyWorkgroup[] = [];

    this.selectedWorkgroups.value.forEach((elem, i) => {
      const found = this.findWorkGroupOption(elem);
      if (found !== undefined) {
        workgroups.push({
          id: found.value,
          name: found.display,
          isStandard: true,
        });
      }
    });

    return workgroups;
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/contact-details/account/account-contact-details.ts